/*
 * Line diff, for comparing two revisions of an article.
 *
 * kb.ArticleRevision keeps whole snapshots, not diffs, so "what changed between
 * revision 3 and revision 4" has to be computed when someone asks. It is asked
 * rarely and over documents of a few hundred lines, so a
 * plain O(n*m) longest-common-subsequence table is the right amount of
 * machinery - and it avoids a dependency in a package the browser loads.
 *
 * A guard rail: above LCS_CELL_LIMIT cells the table is abandoned in favour of a
 * cheap prefix/suffix diff, so a pathological pair of revisions cannot stall the
 * request.
 */
const LCS_CELL_LIMIT = 4_000_000;
export function diffLines(left, right) {
    const leftLines = splitLines(left);
    const rightLines = splitLines(right);
    const approximate = leftLines.length * rightLines.length > LCS_CELL_LIMIT;
    const lines = approximate
        ? diffByAffixes(leftLines, rightLines)
        : diffByLcs(leftLines, rightLines);
    return { lines, summary: summarise(lines, approximate) };
}
/** Only the changed lines, with `context` unchanged lines around each run. */
export function collapseUnchanged(lines, context = 3) {
    const keep = new Set();
    lines.forEach((line, index) => {
        if (line.op === 'equal')
            return;
        for (let i = index - context; i <= index + context; i += 1) {
            if (i >= 0 && i < lines.length)
                keep.add(i);
        }
    });
    const output = [];
    let inGap = false;
    lines.forEach((line, index) => {
        if (keep.has(index)) {
            output.push(line);
            inGap = false;
        }
        else if (!inGap) {
            output.push('gap');
            inGap = true;
        }
    });
    return output;
}
function splitLines(text) {
    return (text ?? '').replace(/\r\n?/g, '\n').split('\n');
}
function diffByLcs(leftLines, rightLines) {
    const rows = leftLines.length;
    const columns = rightLines.length;
    // table[i][j] = length of the LCS of leftLines[i..] and rightLines[j..].
    const table = Array.from({ length: rows + 1 }, () => new Uint32Array(columns + 1));
    for (let i = rows - 1; i >= 0; i -= 1) {
        const current = table[i];
        const next = table[i + 1];
        if (!current || !next)
            continue;
        for (let j = columns - 1; j >= 0; j -= 1) {
            current[j] =
                leftLines[i] === rightLines[j]
                    ? (next[j + 1] ?? 0) + 1
                    : Math.max(next[j] ?? 0, current[j + 1] ?? 0);
        }
    }
    const lines = [];
    let i = 0;
    let j = 0;
    while (i < rows && j < columns) {
        if (leftLines[i] === rightLines[j]) {
            lines.push({ op: 'equal', leftLine: i + 1, rightLine: j + 1, text: leftLines[i] ?? '' });
            i += 1;
            j += 1;
            continue;
        }
        const downward = table[i + 1]?.[j] ?? 0;
        const rightward = table[i]?.[j + 1] ?? 0;
        if (downward >= rightward) {
            lines.push({ op: 'delete', leftLine: i + 1, rightLine: null, text: leftLines[i] ?? '' });
            i += 1;
        }
        else {
            lines.push({ op: 'insert', leftLine: null, rightLine: j + 1, text: rightLines[j] ?? '' });
            j += 1;
        }
    }
    while (i < rows) {
        lines.push({ op: 'delete', leftLine: i + 1, rightLine: null, text: leftLines[i] ?? '' });
        i += 1;
    }
    while (j < columns) {
        lines.push({ op: 'insert', leftLine: null, rightLine: j + 1, text: rightLines[j] ?? '' });
        j += 1;
    }
    return lines;
}
/**
 * Fallback for very large documents: keep the common prefix and suffix, and call
 * everything between them replaced. Correct, just less precise.
 */
function diffByAffixes(leftLines, rightLines) {
    let prefix = 0;
    while (prefix < leftLines.length &&
        prefix < rightLines.length &&
        leftLines[prefix] === rightLines[prefix]) {
        prefix += 1;
    }
    let suffix = 0;
    while (suffix < leftLines.length - prefix &&
        suffix < rightLines.length - prefix &&
        leftLines[leftLines.length - 1 - suffix] === rightLines[rightLines.length - 1 - suffix]) {
        suffix += 1;
    }
    const lines = [];
    for (let i = 0; i < prefix; i += 1) {
        lines.push({ op: 'equal', leftLine: i + 1, rightLine: i + 1, text: leftLines[i] ?? '' });
    }
    for (let i = prefix; i < leftLines.length - suffix; i += 1) {
        lines.push({ op: 'delete', leftLine: i + 1, rightLine: null, text: leftLines[i] ?? '' });
    }
    for (let j = prefix; j < rightLines.length - suffix; j += 1) {
        lines.push({ op: 'insert', leftLine: null, rightLine: j + 1, text: rightLines[j] ?? '' });
    }
    for (let k = 0; k < suffix; k += 1) {
        const leftIndex = leftLines.length - suffix + k;
        const rightIndex = rightLines.length - suffix + k;
        lines.push({
            op: 'equal',
            leftLine: leftIndex + 1,
            rightLine: rightIndex + 1,
            text: leftLines[leftIndex] ?? '',
        });
    }
    return lines;
}
function summarise(lines, approximate) {
    let added = 0;
    let removed = 0;
    let unchanged = 0;
    for (const line of lines) {
        if (line.op === 'insert')
            added += 1;
        else if (line.op === 'delete')
            removed += 1;
        else
            unchanged += 1;
    }
    return { added, removed, unchanged, approximate };
}
//# sourceMappingURL=diff.js.map